import { DeviceService, deviceServiceDiscovered, deviceServicePort, loopbackPermission } from './client';
import { LIBRARY_VENDORS, parseLibraryVersion } from './version';
import { deviceServiceBelowMinimum, MIN_DEVICE_SERVICE_VERSION } from './minimum';

/**
 * Workstation facts worth having on a support ticket.
 *
 * Nearly every Device Service ticket starts with the same three questions — which version, which scanner, which
 * driver — and the answers arrive over two or three emails. Collecting them at the moment the customer asks for
 * help removes that round trip, and the customer is the one person who cannot easily look them up.
 */
export interface Diagnostics {
  collectedAt: string;
  /** Reported Device Service version, or null when it could not be asked. */
  deviceServiceVersion: string | null;
  /** Why there is no version, when there isn't one. Absence of data is itself diagnostic. */
  deviceServiceNote?: string;
  belowMinimum: boolean;
  minimumRequired: string;
  port: number;
  architecture?: string;
  scanners: { name: string; vendor?: string; serial?: string; firmware?: string }[];
  /** Installed scanner libraries by vendor. Absent vendors simply are not installed. */
  libraries: Record<string, string>;
  browser: string;
  platform?: string;
}

/** Never let diagnostics collection outlast the patience of someone trying to file a ticket. */
const BUDGET_MS = 6_000;

const withTimeout = <T,>(p: Promise<T>, ms: number): Promise<T | null> =>
  Promise.race([p.catch(() => null), new Promise<null>((r) => setTimeout(() => r(null), ms))]);

/**
 * Gather what we can, and never throw.
 *
 * Deliberately will NOT start a fresh discovery when the local-network permission is still unanswered: the
 * browser would raise its "allow access to devices on your network" dialog in the middle of a support form,
 * which reads as the site doing something suspicious at exactly the wrong moment. In that case the note says
 * so — which tells the agent something useful in itself.
 */
export async function collectDiagnostics(): Promise<Diagnostics> {
  const base: Diagnostics = {
    collectedAt: new Date().toISOString(),
    deviceServiceVersion: null,
    belowMinimum: false,
    minimumRequired: MIN_DEVICE_SERVICE_VERSION,
    port: deviceServicePort(),
    scanners: [],
    libraries: {},
    browser: navigator.userAgent,
    ...(navigator.platform ? { platform: navigator.platform } : {}),
  };

  if (!deviceServiceDiscovered() && (await loopbackPermission()) === 'prompt') {
    return { ...base, deviceServiceNote: 'Not contacted — this browser has not yet been granted local network access.' };
  }

  const svc = await withTimeout(DeviceService.connect(), BUDGET_MS);
  if (!svc) {
    return { ...base, deviceServiceNote: 'Not reachable from the browser — the service may be stopped, or on a different port.' };
  }

  const [version, bits, scanners] = await Promise.all([
    withTimeout(svc.version(), 3_000),
    withTimeout(svc.bits(), 3_000),
    withTimeout(svc.listScanners(), 4_000),
  ]);

  // Vendor libraries in parallel: four short calls, and a missing one is an answer rather than a failure.
  const libs = await Promise.all(
    LIBRARY_VENDORS.map(async (v) => [v, parseLibraryVersion(await withTimeout(svc.libraryInstalled(v), 3_000))] as const),
  );

  return {
    ...base,
    deviceServiceVersion: version,
    belowMinimum: deviceServiceBelowMinimum(version),
    ...(bits ? { architecture: `${bits}-bit` } : {}),
    scanners: (scanners ?? []).map((d) => ({
      name: d.deviceName,
      ...(d.vendorName ? { vendor: d.vendorName } : {}),
      ...(d.deviceSerial ? { serial: d.deviceSerial } : {}),
      ...(d.deviceVersion ? { firmware: d.deviceVersion } : {}),
    })),
    libraries: Object.fromEntries(libs.filter(([, ver]) => ver)) as Record<string, string>,
  };
}
